<template>
  <div class="impact-strip gm-scroll">
    <article
      v-for="(impact, index) in impacts"
      :key="index"
      class="impact"
      :style="{ '--impact-accent': violationColor(impact.type) }"
    >
      <header class="impact__head">
        <span class="impact__kind">{{ impact.kind }}</span>
        <span class="impact__violation">{{ impact.type }}</span>
      </header>

      <div class="impact__base">
        <span class="impact__base-value">{{ impact.formula.base }}</span>
        <span class="impact__base-label">base</span>
      </div>

      <ul v-if="scalings(impact).length" class="impact__scaling">
        <li v-for="(scale, si) in scalings(impact)" :key="si">
          <span class="impact__stat">{{ scale.stat }}</span>
          <span class="impact__weight">{{ (scale.value * 100).toFixed(1) }}%</span>
        </li>
      </ul>
      <p v-else class="impact__flat">Flat — no stat scaling</p>

      <ul v-if="requires(impact).length" class="impact__requires">
        <li v-for="(req, ri) in requires(impact)" :key="ri">
          Requires {{ req.stat }} {{ req.value }}
        </li>
      </ul>
    </article>
  </div>
</template>

<script setup>
import { VIOLATION_TYPE_COLORS } from '@/types/skillVisualization';

defineProps({
  impacts: { type: Array, required: true },
});

function violationColor(type) {
  const color = VIOLATION_TYPE_COLORS[type];
  if (!color) return 'var(--gm-text-muted)';
  return `rgb(${color.r}, ${color.g}, ${color.b})`;
}

function scalings(impact) {
  return impact?.formula?.scaling ?? [];
}

function requires(impact) {
  return impact?.formula?.requires ?? [];
}
</script>

<style scoped>
.impact-strip {
  display: flex;
  gap: var(--gm-gap-sm);
  overflow-x: auto;
  overflow-y: hidden;
  padding: var(--gm-gap-sm) var(--gm-gap);
}

.impact {
  flex: 0 0 auto;
  min-width: 12rem;
  max-width: 18rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  padding: var(--gm-gap-sm);
  background: var(--gm-surface-2);
  border: 1px solid var(--gm-border-dim);
  border-left: 3px solid var(--impact-accent);
  border-radius: var(--gm-radius-sm);
}

.impact__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--gm-gap-sm);
}

.impact__kind {
  font-family: var(--gm-font-display);
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--gm-text);
}

.impact__violation {
  font-size: 0.58rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--impact-accent);
}

.impact__base {
  display: flex;
  align-items: baseline;
  gap: 0.3rem;
}

.impact__base-value {
  font-family: var(--gm-font-display);
  font-size: 1.1rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--impact-accent);
}

.impact__base-label {
  font-size: 0.58rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--gm-text-dim);
}

.impact__scaling,
.impact__requires {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.impact__scaling li {
  display: flex;
  justify-content: space-between;
  gap: var(--gm-gap-sm);
  font-size: 0.68rem;
}

.impact__stat {
  color: var(--gm-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.impact__weight {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: var(--gm-text);
}

.impact__flat,
.impact__requires li {
  margin: 0;
  font-size: 0.65rem;
  font-style: italic;
  color: var(--gm-text-dim);
}
</style>
